import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import Dashboard from "@/pages/Dashboard";
import CampaignWorkspace from "@/pages/CampaignWorkspace";
import AssetsPage from "@/pages/AssetsPage";
import AssetWorkspace from "@/pages/AssetWorkspace";
import APIPage from "@/pages/APIPage";
import BucketListPage from "@/pages/BucketListPage";
// Fast Campaigns (isolated feature)
import FastCampaignsPage from "@/pages/fast-campaigns/FastCampaignsPage";
import CreateFastCampaignPage from "@/pages/fast-campaigns/CreateFastCampaignPage";
import FastCampaignResultsPage from "@/pages/fast-campaigns/FastCampaignResultsPage";
// Plans (isolated feature)
import PlanPage from "@/pages/plans/PlanPage";
// Bucket Workflow (Kanban board)
import { BucketWorkflowPage } from "@/pages/bucket-workflow";
import { Loader2 } from "lucide-react";

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Auth Route wrapper (redirects to dashboard if logged in)
function AuthRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={
        <AuthRoute>
          <LoginPage />
        </AuthRoute>
      } />
      <Route path="/signup" element={
        <AuthRoute>
          <SignupPage />
        </AuthRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/campaign/:id" element={
        <ProtectedRoute>
          <CampaignWorkspace />
        </ProtectedRoute>
      } />
      <Route path="/assets" element={
        <ProtectedRoute>
          <AssetsPage />
        </ProtectedRoute>
      } />
      <Route path="/assets/:id" element={
        <ProtectedRoute>
          <AssetWorkspace />
        </ProtectedRoute>
      } />
      <Route path="/developers" element={
        <ProtectedRoute>
          <APIPage />
        </ProtectedRoute>
      } />
      <Route path="/potential-campaigns" element={
        <ProtectedRoute>
          <BucketListPage />
        </ProtectedRoute>
      } />
      {/* Fast Campaigns Routes (isolated) */}
      <Route path="/fast-campaigns" element={
        <ProtectedRoute>
          <FastCampaignsPage />
        </ProtectedRoute>
      } />
      <Route path="/fast-campaigns/new" element={
        <ProtectedRoute>
          <CreateFastCampaignPage />
        </ProtectedRoute>
      } />
      <Route path="/fast-campaigns/:id" element={
        <ProtectedRoute>
          <FastCampaignResultsPage />
        </ProtectedRoute>
      } />
      {/* Plans Routes (isolated) */}
      <Route path="/plans/:planId" element={
        <ProtectedRoute>
          <PlanPage />
        </ProtectedRoute>
      } />
      {/* Bucket Workflow (Kanban board) */}
      <Route path="/workflow" element={
        <ProtectedRoute>
          <BucketWorkflowPage />
        </ProtectedRoute>
      } />
      <Route path="/bucket-list" element={
        <ProtectedRoute>
          <BucketWorkflowPage />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-white">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
